import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { approvalsApi, type PendingApproval } from '../api/client'
import { Button, Card, EmptyState, PageHeader } from '../components/ui'

export default function AdminApprovalsPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const list = useQuery({
    queryKey: ['admin', 'instance-approvals'],
    queryFn: approvalsApi.listPending,
    refetchInterval: 30_000,
  })
  const approve = useMutation({
    mutationFn: (id: string) => approvalsApi.approve(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'instance-approvals'] }),
  })
  const reject = useMutation({
    mutationFn: (id: string) => approvalsApi.reject(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'instance-approvals'] }),
  })

  const items = list.data ?? []

  return (
    <div className="p-4 sm:p-8 mx-auto w-full max-w-5xl">
      <PageHeader title={t('admin.approvals.title')} description={t('admin.approvals.subtitle')} />
      <Card padded={false} className="divide-y divide-gray-100">
        {items.length === 0 && <EmptyState title={t('admin.approvals.empty')} />}
        {items.map((a) => (
          <Row
            key={a.id}
            item={a}
            pending={approve.isPending || reject.isPending}
            onApprove={() => approve.mutate(a.id)}
            onReject={() => reject.mutate(a.id)}
          />
        ))}
      </Card>
    </div>
  )
}

function Row({ item, pending, onApprove, onReject }: {
  item: PendingApproval
  pending: boolean
  onApprove: () => void
  onReject: () => void
}) {
  const { t } = useTranslation()
  const sectorLabel = item.sector ? t(`create.free.sectors.${item.sector}`, item.sector) : '-'
  return (
    <div className="px-4 py-3 flex flex-wrap items-start gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900">
          {item.name} <span className="text-gray-400 font-normal">· {item.subdomain}.provisyer.com</span>
        </div>
        <div className="text-xs text-gray-500 mt-0.5">
          {item.created_by_email || '-'} · Odoo {item.odoo_version} · {new Date(item.created_at).toLocaleString()}
        </div>
        <div className="text-xs text-gray-600 mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
          <span>{t('admin.approvals.sector')}: <span className="font-medium">{sectorLabel}</span></span>
          <span>{t('admin.approvals.users')}: <span className="font-medium">{item.user_count || '-'}</span></span>
        </div>
        {item.request_reason && (
          <div className="text-xs text-gray-500 mt-1 italic">“{item.request_reason}”</div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button variant="success" size="sm" onClick={onApprove} disabled={pending}>
          {t('admin.approvals.approve')}
        </Button>
        <Button variant="danger" size="sm" onClick={onReject} disabled={pending}>
          {t('admin.approvals.reject')}
        </Button>
      </div>
    </div>
  )
}
